'use client';

import type { FC, ReactNode } from 'react';
import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, EmptyState } from '../../components/ui';
import { GiOpenChest } from '../../components/icons';
import { CaseCard } from '../../components/cases/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { CaseCatalog } from '../../components/cases/catalog/CaseCatalog';
import type { CaseCatalog as CaseCatalogData, CatalogCase, CatalogSort } from '../../components/cases/catalog/catalog.types';
import { ALL_CATEGORIES, readStoredCatalogSort, storeCatalogSort } from '../../components/cases/catalog/catalogSort';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCaseLinkHref } from '../../hooks/useCaseLinkHref';
import { useCasesCatalogQuery } from '../../queries/useCasesQuery';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import translations from './CasesScreen.translations';

const GRID_MOBILE = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,150px),1fr))] gap-2';
const GRID_DESKTOP = 'grid grid-cols-4 gap-3';
const FIRST_ROW_MOBILE = 2;
const FIRST_ROW_DESKTOP = 4;
// WHY: ширина карточки по сетке выше: десктоп — 4 колонки в колонке 896 px, мобильная — две колонки до 498 px, дальше
// колонок больше, а карточка не шире 230 px
const CARD_IMAGE_SIZES = '(min-width: 1024px) 208px, (min-width: 498px) 230px, calc(50vw - 20px)';

interface CasesScreenProps {
  params?: { category?: string };
  catalog?: CaseCatalogData;
  initialSort?: CatalogSort;
  seoSlot?: ReactNode;
}

const readCategoryFromLocation = (): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('category');
};

export const CasesScreen: FC<CasesScreenProps> = ({ params, catalog: catalogOverride, initialSort, seoSlot }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = useCasesCatalogQuery();
  const isDesktop = useIsDesktop();

  const catalog = useMemo<CaseCatalogData>(() => catalogOverride ?? data ?? { general: [], categories: [] }, [catalogOverride, data]);
  const isEmpty = catalog.general.length === 0;
  const showSkeleton = isLoading && !catalogOverride && isEmpty;

  const [sort, setSort] = useState<CatalogSort>(() => initialSort ?? readStoredCatalogSort());
  const handleSortChange = useCallback((next: CatalogSort) => {
    setSort(next);
    storeCatalogSort(next);
  }, []);

  const paramsCategory = params?.category;
  const [category, setCategory] = useState<string>(paramsCategory ?? ALL_CATEGORIES);
  useEffect(() => {
    const next = paramsCategory ?? readCategoryFromLocation();
    if (next && next !== category) setCategory(next);
  }, [paramsCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryChange = useCallback(
    (slug: string) => {
      setCategory(slug);
      navigation.updateCurrentParams({ category: slug === ALL_CATEGORIES ? undefined : slug });
    },
    [navigation],
  );

  const handleCaseClick = useCallback(
    (caseId: string) => {
      navigation.navigate('case-details', { caseId });
    },
    [navigation],
  );
  const caseLink = useCaseLinkHref();
  const firstRow = isDesktop ? FIRST_ROW_DESKTOP : FIRST_ROW_MOBILE;

  const renderCase = useCallback(
    (caseItem: CatalogCase, index: number) => (
      <CaseCard
        key={caseItem.id}
        id={caseItem.id}
        name={caseItem.name}
        imageUrl={caseItem.imageUrl}
        imagePlaceholder={caseItem.imagePlaceholder}
        priceInCredits={caseItem.priceInCredits}
        openingsCount={caseItem.openingsCount}
        isNew={caseItem.isNew}
        isPopular={caseItem.isPopular}
        href={caseLink(caseItem)}
        onClick={handleCaseClick}
        priority={index < firstRow}
        imageSizes={CARD_IMAGE_SIZES}
      />
    ),
    [handleCaseClick, caseLink, firstRow],
  );

  if (showSkeleton) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent scrollRef={scrollRef} scrollKey="cases">
          <div className={`px-4 pt-4 ${isDesktop ? GRID_DESKTOP : GRID_MOBILE}`}>
            {[...Array(6)].map((_, index) => (
              <CaseCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        </ScreenContent>
      </>
    );
  }

  if (error && isEmpty) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent>
          <ErrorMessage onRetry={() => refetch()} />
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent scrollRef={scrollRef} scrollKey="cases">
        {isEmpty ? (
          <EmptyState
            icon={<GiOpenChest size={48} className="text-white/20" />}
            title={t('empty')}
            description={t('emptyHint')}
            action={
              <Button size="l" onClick={() => navigation.navigate('games')}>
                {t('emptyCta')}
              </Button>
            }
          />
        ) : (
          <div className="px-4 pt-4">
            <CaseCatalog
              catalog={catalog}
              sort={sort}
              onSortChange={handleSortChange}
              category={category}
              onCategoryChange={handleCategoryChange}
              gridClassName={isDesktop ? GRID_DESKTOP : GRID_MOBILE}
              renderCase={renderCase}
            />
          </div>
        )}
        {seoSlot && <div className="px-4 pb-8 pt-6">{seoSlot}</div>}
      </ScreenContent>
    </>
  );
};
